/**
 * Maps the jeweller theme from `/api/v1/app-theme/get-theme`
 * into CSS values. Fallbacks match the customer mobile app
 * (ThemeConfig + ThemeMixin + GradientScaffold).
 */

const CONTAINER_FALLBACK = ["#00203f", "#00203f"];
const SCAFFOLD_GRADIENT_FALLBACK = ["#FFFFFF", "#F5F5F5"];

export const DEFAULT_THEME_COLORS = {
  container: "#00203f",
  primary: "#44745E",
  scaffold: "#FFFFFF",
  text: "#000000",
  scaffoldText: "#000000",
  border: "rgba(0, 0, 0, 0.15)",
  appBar: "#fafeff",
  appBarText: "#000000",
  svgColor: "#ece4e4",
  bottomNavBg: "#011D31",
  bottomNavSelected: "#00203F",
  bottomNavUnselected: "#F6F6F6",
  amountOption: "#44745E",
  splash: "#ffffff",
  otpBackground: "",
  themeType: "",
};

function colorList(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => typeof item === "string" && item.trim());
}

function gradientCss(colors, fallback, direction) {
  const source = colorList(colors);
  const list = source.length ? source : fallback;
  const first = list[0] || fallback[0];
  const rest = list.length > 1 ? list.slice(1) : [first];
  return `linear-gradient(${direction}, ${first}, ${rest.join(", ")})`;
}

function colorOr(value, fallback) {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function resolvedDefaults() {
  return {
    scaffoldGradientColors: gradientCss(
      [],
      ["#FFFFFF", "#FFFFFF"],
      "to bottom",
    ),
    bigContainer: gradientCss([], CONTAINER_FALLBACK, "135deg"),
    mediumContainer: gradientCss([], CONTAINER_FALLBACK, "135deg"),
    smallContainer: gradientCss([], CONTAINER_FALLBACK, "135deg"),
    ...DEFAULT_THEME_COLORS,
  };
}

export function resolveThemeColors(theme) {
  if (!theme) return resolvedDefaults();

  const source = theme;
  const scaffoldColors = colorList(source.scaffoldGradientColors);
  const scaffoldGradient =
    scaffoldColors.length >= 2
      ? gradientCss(scaffoldColors, SCAFFOLD_GRADIENT_FALLBACK, "to bottom")
      : colorOr(source.scaffoldColor, "")
        ? colorOr(source.scaffoldColor, DEFAULT_THEME_COLORS.scaffold)
        : gradientCss([], SCAFFOLD_GRADIENT_FALLBACK, "to bottom");

  return {
    scaffoldGradientColors: scaffoldGradient,
    bigContainer: gradientCss(
      source.bigContainerGradientColors,
      CONTAINER_FALLBACK,
      "135deg",
    ),
    mediumContainer: gradientCss(
      source.mediumContainerGradientColors,
      CONTAINER_FALLBACK,
      "135deg",
    ),
    smallContainer: gradientCss(
      source.smallContainerGradientColors,
      CONTAINER_FALLBACK,
      "135deg",
    ),
    container: colorOr(source.containerColor, DEFAULT_THEME_COLORS.container),
    primary: colorOr(source.primaryColor, DEFAULT_THEME_COLORS.primary),
    scaffold: colorOr(source.scaffoldColor, DEFAULT_THEME_COLORS.scaffold),
    text: colorOr(source.textColor, DEFAULT_THEME_COLORS.text),
    scaffoldText: colorOr(
      source.scaffoldTextColor,
      colorOr(source.textColor, DEFAULT_THEME_COLORS.scaffoldText),
    ),
    border: colorOr(source.containerBorder, DEFAULT_THEME_COLORS.border),
    appBar: colorOr(source.appBarColor, DEFAULT_THEME_COLORS.appBar),
    appBarText: colorOr(source.appBarTextColor, DEFAULT_THEME_COLORS.appBarText),
    svgColor: colorOr(source.svgColor, DEFAULT_THEME_COLORS.svgColor),
    bottomNavBg: colorOr(
      source.bottomNavBarBackgroundColor,
      DEFAULT_THEME_COLORS.bottomNavBg,
    ),
    bottomNavSelected: colorOr(
      source.bottomNavBarSelectedColor,
      DEFAULT_THEME_COLORS.bottomNavSelected,
    ),
    bottomNavUnselected: colorOr(
      source.bottomNavBarUnselectedColor,
      DEFAULT_THEME_COLORS.bottomNavUnselected,
    ),
    amountOption: colorOr(
      source.amountOptionColor,
      colorOr(source.primaryColor, DEFAULT_THEME_COLORS.amountOption),
    ),
    splash: colorOr(source.splashColor, DEFAULT_THEME_COLORS.splash),
    otpBackground: colorOr(source.otpScreenBackgroundColor, ""),
    themeType: colorOr(source.themeType, ""),
  };
}

export function pageBackground(themeColors) {
  return themeColors.otpBackground || themeColors.scaffoldGradientColors;
}

export function primaryButtonSx(themeColors, extra = {}) {
  return {
    background: themeColors.primary,
    color: themeColors.text,
    fontWeight: 500,
    textTransform: "none",
    "&:hover": {
      background: themeColors.primary,
      opacity: 0.92,
    },
    ...extra,
  };
}
